import React from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../i18n/bookingCopy';
import { formatBookingCreatedAt } from '../utils/bookingInstant';

export interface BookingCreatedAtLabelProps {
  createdAt?: string | null;
  /** Booking IANA timezone from the same response; used for a short zone label. */
  timezone?: string | null;
  /**
   * `inline` — next to other body copy.
   * `footer` — quiet meta line at the bottom of a card (default for cards).
   */
  placement?: 'inline' | 'footer';
  /** When false, omit the short timezone suffix (preferred on cards). Default true for footer. */
  showTimezone?: boolean;
  variant?: TypographyProps['variant'];
  sx?: TypographyProps['sx'];
}

/**
 * Renders booking creation time from the API `createdAt` field (source of truth).
 * Returns null when the field is missing so older payloads degrade gracefully.
 */
const BookingCreatedAtLabel: React.FC<BookingCreatedAtLabelProps> = ({
  createdAt,
  timezone,
  placement = 'inline',
  showTimezone,
  variant = 'caption',
  sx,
}) => {
  const { t, i18n } = useTranslation();
  if (!createdAt?.trim()) {
    return null;
  }

  const includeTimezone = showTimezone ?? placement !== 'footer';
  const formatted = formatBookingCreatedAt(
    createdAt,
    i18n.language,
    includeTimezone ? timezone : undefined,
  );

  const isFooter = placement === 'footer';

  return (
    <Typography
      variant={variant}
      color="text.secondary"
      sx={{
        ...(isFooter
          ? {
              display: 'block',
              m: 0,
              fontSize: '0.7rem',
              lineHeight: 1.35,
              letterSpacing: '0.01em',
              opacity: 0.85,
              textAlign: 'right',
              width: '100%',
            }
          : null),
        ...sx,
      }}
    >
      {t('bookings.bookedAt', 'Booked {{date}}', { date: formatted })}
    </Typography>
  );
};

export default BookingCreatedAtLabel;
